import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { PICFLOW_CONFIG } from '../picflow-config';
import { BrowserService } from './browser.service';
import { LazyLoadedSentryService } from './lazy-loaded-sentry-service';

const SAMPLE_RAND_KEY = 'sentry_sample_rand';

/**
 * Decides once per browser session whether to trace, so Sentry (lazy-loaded for errors) is only loaded up front in
 * sampled sessions. Every trace of a sampled session reuses the session's `sampleRand`, so the session stays traced
 * and Sentry extrapolates counts with the route's rate.
 */
@Injectable({
  providedIn: 'root',
})
export class SentryTracingService {
  private config = inject(PICFLOW_CONFIG);
  private browserService = inject(BrowserService);
  private lazyLoadedSentryService = inject(LazyLoadedSentryService);
  private platformId = inject(PLATFORM_ID);

  /** Set in sampled browser sessions only. */
  readonly sampling = this.decide();

  start() {
    if (this.sampling) void this.lazyLoadedSentryService.load();
  }

  private decide(): { rate: number; sampleRand: number } | undefined {
    if (!isPlatformBrowser(this.platformId) || this.browserService.isExtension()) return;
    // Public galleries on tenant domains are most of the traffic; the app itself is where traces help most.
    const rate = this.config.env !== 'production' ? 1 : this.browserService.isTenantDomain() ? 0.01 : 0.1;
    const sampleRand = this.sessionSampleRand();
    return sampleRand < rate ? { rate, sampleRand } : undefined;
  }

  private sessionSampleRand(): number {
    try {
      const stored = Number(sessionStorage.getItem(SAMPLE_RAND_KEY) ?? NaN);
      if (stored >= 0 && stored < 1) return stored;
      const sampleRand = Math.random();
      sessionStorage.setItem(SAMPLE_RAND_KEY, String(sampleRand));
      return sampleRand;
    } catch {
      return Math.random();
    }
  }
}
